import {
  AlertModal,
  Box,
  Checkbox,
  Input,
  Link,
  InfiniteTable,
  Pill,
  Popover,
  Select,
  Separator,
  Stack,
  Button as CommonUiButton,
  Text,
} from '@sixfold/common-ui';
import {
  BackofficeScope,
  BackofficeScopeTooltips,
  hasOneOfScopes,
  ReadOnlyRoleEquivalentScopes,
} from '@sixfold/session-interface';
import { notNil } from '@sixfold/typed-primitives';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { NavLink } from 'react-router';

import { CUSTOMER_PRODUCT_NAME } from '../../../constants';
import { Button } from '../../components/button';
import { ConfirmButton } from '../../components/confirm_button';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { Form as FormFactory } from '../../lib/form';
import {
  AddCredentialsToUserInput,
  BackofficeRole,
  CredentialType,
  RemoveCredentialsFromUserInput,
  UserQuery,
} from '../../lib/graphql';
import { Table } from '../../lib/table';
import { Omit } from '../../lib/types';
import { generateRandomHexString } from '../../lib/util/string';
import { customSlugify } from '../../lib/util/url';
import { Routes } from '../../routes';

import styles from './user.module.css';

const tpRtvApiDocumentationUrl =
  'https://transporeon-hcskb.atlassian.net/wiki/spaces/ADPD/pages/27234193/Send+Visibility+Data+from+your+Inhouse+System';

type CompanyType = NonNullable<Required<UserQuery['user']>>['allCompanies'][number];
class CompaniesTable extends Table<CompanyType> {}

export type UserFormData = {
  first_name?: string;
  last_name?: string;
  email: string;
  locale?: string;
  backoffice_role?: string;
  backoffice_scopes: string[] | null;
  auth_service?: string;
};

interface UserMutationInput extends Omit<UserFormData, 'backoffice_role'> {
  backoffice_role: BackofficeRole | null;
}

export interface UserViewDataProps {
  data?: {
    user: UserQuery['user'] | null | undefined;
  };
}

interface RouteProps {
  routeProps: {
    push: (path: string) => void;
    goBack: () => void;
  };
}

type Props = UserViewDataProps &
  RouteProps & {
    createUser?: (authService: string, input: UserMutationInput) => Promise<void>;
    updateUser?: (userId: string, input: UserMutationInput) => Promise<void>;
    deleteUser?: (userId: string) => Promise<void>;
    removeCredentialsFromUser?: (input: RemoveCredentialsFromUserInput) => Promise<void>;
    addCredentialsToUser?: (input: AddCredentialsToUserInput) => Promise<void>;
    isNewUser: boolean;
    scopes: string[];
  };

type UserScopes = {
  isAuthorized: boolean;
  scope: string;
  id: string;
}[];

class Form extends FormFactory<UserFormData> {}

const backofficeRoleOptions = [
  { value: '', label: 'None' },
  { value: BackofficeRole.readonly, label: 'Read Only' },
  { value: BackofficeRole.admin, label: 'Admin' },
];

const LastActivityContextPopover = () => {
  return (
    <>
      Last active in {CUSTOMER_PRODUCT_NAME} UI
      <Popover placement={'top'} content={<Stack direction="column">Tracking since 28.08.2025</Stack>}>
        <CommonUiButton iconEnd="small-info-outline" size="xsmall" kind="ghost" hideLabel>
          Help
        </CommonUiButton>
      </Popover>
    </>
  );
};

export const UserView: React.FunctionComponent<Props> = ({
  routeProps,
  createUser,
  updateUser,
  deleteUser,
  removeCredentialsFromUser,
  addCredentialsToUser,
  data,
  isNewUser,
  scopes,
}) => {
  const user = data?.user ?? null;

  const isAdmin = useIsAdmin();
  const hasUserEditCredentialsPermission = useHasScopes([BackofficeScope.userWriteCredentials]);
  const hasUserReadPermission = useHasScopes([BackofficeScope.userReadUser]);

  const isDisabled = !isAdmin;
  const userCanEditCredentials = isAdmin || hasUserEditCredentialsPermission;

  const [userScopes, setUserScopes] = useState(
    scopes.map((scope) => ({
      scope,
      isAuthorized: hasOneOfScopes(user?.backoffice_scopes ?? [], [scope]),
      id: scope.toString(),
    })),
  );
  const [userRole, setUserRole] = useState(
    isNewUser ? '' : user !== null && notNil(user.backoffice_role) ? user.backoffice_role : '',
  );
  const [showLegacyApiWarningModal, setShowLegacyApiWarningModal] = useState(false);

  if (!isNewUser && user === null) {
    return <div>This user does not exist</div>;
  }

  return (
    <React.Fragment>
      {user !== null && (
        <h3>
          <span style={{ marginRight: 20 }}>{`#${user.user_id} ${user.first_name} ${user.last_name}`.trim()}</span>
        </h3>
      )}
      {user !== null && user.created_at && (
        <div>
          <strong>Created at:</strong> <FormattedDate date={user.created_at} />
        </div>
      )}
      {user !== null && (
        <div style={{ marginBottom: 20 }}>
          <strong>Last active in {CUSTOMER_PRODUCT_NAME} UI:</strong> <FormattedDate date={user.lastUiActivityAt} />
        </div>
      )}
      <Form
        validateFields={{ first_name: { exist: 'Missing first name' }, email: { exist: 'Missing email' } }}
        initialValues={{
          first_name: user?.first_name ?? '',
          last_name: user?.last_name ?? '',
          email: user?.email ?? '',
          locale: user?.locale ?? '',
          backoffice_role: userRole,
          backoffice_scopes: isNewUser ? null : (user?.backoffice_scopes ?? null),
        }}
        onSubmit={async (form) => {
          if (isNewUser && createUser !== undefined) {
            await createUser(form.auth_service ?? 'google', {
              first_name: form.first_name?.trim(),
              last_name: form.last_name?.trim(),
              email: form.email.trim(),
              locale: form.locale?.trim(),
              backoffice_role: convertRole(userRole),
              backoffice_scopes: null,
            });
          }

          if (user !== null && updateUser !== undefined) {
            await updateUser(user.user_id, {
              first_name: form.first_name?.trim(),
              last_name: form.last_name?.trim(),
              email: form.email.trim(),
              locale: form.locale?.trim(),
              backoffice_role: convertRole(userRole),
              backoffice_scopes: userScopes.filter((s) => s.isAuthorized).map((s) => s.scope),
            });
          }

          return routeProps.push('/users');
        }}>
        {({ form, errors, onChange, onSubmit, isSubmittingForm }) => {
          return (
            <form onSubmit={onSubmit}>
              <Stack direction="row" gap="medium" style={{ marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <Input
                    label={errors.first_name ?? 'First Name'}
                    invalid={errors.first_name !== undefined}
                    name="first_name"
                    placeholder="First Name"
                    value={form.first_name}
                    onChange={({ event }) => onChange(event)}
                    disabled={isDisabled}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    label="Last name"
                    name="last_name"
                    placeholder="Last Name"
                    value={form.last_name}
                    onChange={({ event }) => onChange(event)}
                    disabled={isDisabled}
                  />
                </div>
              </Stack>
              <Stack direction="row" gap="medium" style={{ marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={({ event }) => onChange(event)}
                    disabled={isDisabled}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    label="Locale"
                    name="locale"
                    placeholder="Locale"
                    value={form.locale}
                    onChange={({ event }) => onChange(event)}
                    disabled={isDisabled}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Select
                    label="Backoffice role"
                    labelPosition="block"
                    size="large"
                    name="backoffice_role"
                    value={backofficeRoleOptions.find((o) => o.value === userRole) ?? backofficeRoleOptions[0]}
                    options={backofficeRoleOptions}
                    onChange={(option) => {
                      const value = option?.value ?? '';
                      selectRoleRelevantScopes(value, userScopes, setUserScopes);
                      setUserRole(value);
                    }}
                    disabled={isDisabled}
                  />
                </div>
              </Stack>
              <div className="field">
                <div className="small secondary scope__header" style={{ alignItems: 'center' }}>
                  <h3 className="table__header" style={{ margin: 0 }}>
                    Backoffice user permissions
                  </h3>
                  <Popover
                    content={
                      <>
                        <p>
                          Backoffice permissions give users access to specific parts of backoffice. Useful when a user
                          should be able to view/edit some parts without having to give them the all-encompassing
                          &apos;admin&apos; role.
                        </p>
                        <p>
                          <span style={{ fontWeight: 'bold' }}>{'NB! '}</span>
                          Currently, users with &apos;admin&apos; role have access to everything in backoffice
                          regardless of which permissions they have and users without any roles have limited access
                          regardless of their permissions. So, in practice, currently the permissions are mainly useful
                          for &apos;readonly&apos; role users who should have access to some additional parts of
                          backoffice the role doesn&apos;t grant them.
                        </p>
                      </>
                    }>
                    <CommonUiButton
                      iconEnd="small-info-outline"
                      size="xsmall"
                      kind="ghost"
                      hideLabel
                      className="permission-tip">
                      Permissions help
                    </CommonUiButton>
                  </Popover>
                </div>
                <Stack direction="row" alignItems="center" style={{ marginBottom: 10 }}>
                  <Text>Presets:</Text>
                  <Button
                    type="button"
                    size="xsmall"
                    title={'Read Only: Access to backoffice information in read-only mode'}
                    onClick={() => setPermissionsAndRole(1, userScopes, setUserScopes, setUserRole)}>
                    Level 1
                  </Button>
                  <Button
                    type="button"
                    size="xsmall"
                    title={'Read+Write: Access to backoffice information with the ability to edit it'}
                    onClick={() => setPermissionsAndRole(2, userScopes, setUserScopes, setUserRole)}>
                    Level 2
                  </Button>
                  <Button
                    type="button"
                    size="xsmall"
                    title={
                      'Read+Write including credentials: Access to backoffice information including FMS credentials and adding/removing API keys and being able to edit them'
                    }
                    onClick={() => setPermissionsAndRole(3, userScopes, setUserScopes, setUserRole)}>
                    Level 3
                  </Button>
                </Stack>
                <Box
                  style={{
                    height: 250,
                  }}>
                  <InfiniteTable
                    columns={[
                      {
                        id: 'scope',
                        title: 'Scope',
                        width: '40%',
                        cellContent: (data) => {
                          const tooltipText =
                            BackofficeScopeTooltips[data.row.scope as keyof typeof BackofficeScopeTooltips];

                          return (
                            <div
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                const newScopes = [...userScopes];
                                newScopes[data.rowIndex].isAuthorized = !newScopes[data.rowIndex].isAuthorized;
                                setUserScopes(newScopes);
                              }}>
                              <span>{data.row.scope}</span>{' '}
                              {tooltipText && (
                                <Popover content={<p>{tooltipText}</p>}>
                                  <CommonUiButton iconEnd="small-info-outline" size="xsmall" kind="ghost" hideLabel>
                                    Scope help
                                  </CommonUiButton>
                                </Popover>
                              )}
                            </div>
                          );
                        },
                      },
                      {
                        id: 'isAuthorized',
                        title: 'Authorized',
                        width: 'auto',
                        cellContent: (data) => {
                          return (
                            <Checkbox
                              hideLabel
                              label={data.row.scope}
                              checked={data.value}
                              onChange={(e) => {
                                const newScopes = [...userScopes];
                                newScopes[data.rowIndex].isAuthorized = e.checked;
                                setUserScopes(newScopes);
                              }}
                            />
                          );
                        },
                      },
                    ]}
                    data={userScopes}
                  />
                </Box>
              </div>
              {isNewUser && (
                <div style={{ marginBottom: 12, maxWidth: '33%' }}>
                  <Select
                    label="Auth service"
                    name="auth_service"
                    value={{ value: 'google', label: 'Google' }}
                    options={[{ value: 'google', label: 'Google' }]}
                    disabled
                  />
                </div>
              )}
              <Stack direction="row" alignItems="center">
                <Button
                  type="submit"
                  kind="primary"
                  loading={isSubmittingForm}
                  disabled={isDisabled || isSubmittingForm}>
                  {createUser !== undefined ? 'Create' : 'Update'}
                </Button>
                <Button type="button" onClick={() => routeProps.goBack()}>
                  Cancel
                </Button>

                {user !== null && deleteUser !== undefined && (
                  <ConfirmButton
                    disabled={isDisabled}
                    initialButtonColor="red"
                    label="Delete user"
                    className="right floated"
                    onConfirm={async () => {
                      await deleteUser(user.user_id);
                      routeProps.goBack();
                    }}
                  />
                )}
              </Stack>
            </form>
          );
        }}
      </Form>
      <div style={{ margin: '1rem 0' }}>
        <Separator />
      </div>

      <div>
        <h3 className="table__header">User companies</h3>
        <CompaniesTable
          className="ui-table compact-table"
          data={(user?.allCompanies ?? []).map((company) => ({
            ...company,
            lastUiActivityAt_sort: company.user?.lastUiActivityAt ?? new Date(0).toISOString(),
          }))}
          tableHeaders={{
            columns: [
              {
                keyPath: 'company_id',
                value: 'Company ID',
              },
              {
                keyPath: 'company_name',
                value: 'Name',
              },
              {
                value: 'User in company',
              },
              {
                value: 'User on platforms',
              },
              {
                keyPath: 'lastUiActivityAt_sort',
                value: <LastActivityContextPopover />,
              },
            ],
          }}>
          {({ row: { data: companyData, index } }) => (
            <tr key={index}>
              <td>{companyData.company_id}</td>
              <td>
                <NavLink
                  to={Routes.Company.generatePath({
                    company_id: companyData.company_id,
                    slug_company_name: customSlugify(companyData.company_name),
                  })}>
                  <span>{companyData.company_name}</span>
                </NavLink>
              </td>
              <td>
                {user && (
                  <NavLink
                    to={Routes.CompanyUser.generatePath({
                      company_id: companyData.company_id,
                      user_id: user.user_id,
                      slug_company_name: customSlugify(companyData.company_name),
                    })}>
                    <span>View user in company</span>
                  </NavLink>
                )}
              </td>
              <td>
                {companyData.user &&
                  companyData.user.userOnPlatforms.map((uop) => (
                    <Link
                      key={`${uop.platform.platform_id}-${uop.platform.platform_id}`}
                      href={Routes.CompanyPlatform.generatePath({
                        company_id: companyData.company_id,
                        platform_id: uop.platform.platform_id,
                      })}>
                      <Pill kind="neutral">
                        {uop.platform.platform_name}:{uop.userIdOnPlatform}
                      </Pill>
                    </Link>
                  ))}
              </td>
              <td>
                {notNil(companyData.user?.lastUiActivityAt) ? (
                  <FormattedDate date={companyData.user.lastUiActivityAt} />
                ) : (
                  '-'
                )}
              </td>
            </tr>
          )}
        </CompaniesTable>
      </div>

      {(isAdmin || hasUserReadPermission) && user !== null ? (
        <React.Fragment>
          <div style={{ margin: '1rem 0' }}>
            <Separator />
          </div>
          <div>
            <Stack direction="row" alignItems="center">
              <h3 style={{ margin: 0 }}>Credentials</h3>
              {addCredentialsToUser !== undefined && (
                <Button
                  size="small"
                  disabled={!userCanEditCredentials}
                  onClick={() => {
                    setShowLegacyApiWarningModal(true);
                  }}>
                  Add API key (legacy)
                </Button>
              )}
              {addCredentialsToUser !== undefined && (
                <Button
                  size="small"
                  disabled={!userCanEditCredentials}
                  onClick={async () => {
                    await addCredentialsToUser({
                      userId: user.user_id,
                      type: CredentialType.EXT_SHIPMENT_STATUS_API_KEY,
                      value: generateRandomHexString(40),
                    });
                  }}>
                  Add External Status API key
                </Button>
              )}
            </Stack>
            {addCredentialsToUser !== undefined && (
              <div className={styles.legacyApiNote}>
                <div>
                  <strong>Note:</strong> Creating an API key is deprecated. Please use the{' '}
                  <a href={tpRtvApiDocumentationUrl} target="_blank" rel="noopener noreferrer">
                    TP RTV API
                  </a>{' '}
                  for all new integrations.
                </div>
              </div>
            )}
          </div>
          {(user.credentials ?? []).length === 0 ? (
            <Text>No credentials</Text>
          ) : (
            <Stack direction="column" gap="small">
              {(user.credentials ?? []).map((credential) => (
                <Stack key={`${credential.type}:${credential.value}`} direction="row" alignItems="center" gap="small">
                  {credential.type === 'API_KEY' && <Pill kind="info">API key</Pill>}
                  {credential.type === 'EXT_SHIPMENT_STATUS_API_KEY' && <Pill kind="info">Ext API key</Pill>}
                  <Text>{credential.value ?? '-'}</Text>
                  {credential.addedByUserId !== undefined && (
                    <NavLink to={Routes.Users.generatePath({ user_id: credential.addedByUserId })}>
                      #{credential.addedByUserId}
                    </NavLink>
                  )}
                  <FormattedDate date={credential.addedAt} />
                  {removeCredentialsFromUser !== undefined && (
                    <ConfirmButton
                      disabled={!userCanEditCredentials}
                      initialButtonColor="red"
                      onConfirm={async () => {
                        await removeCredentialsFromUser({
                          userId: user.user_id,
                          value: credential.value ?? '',
                          type: credential.type,
                        });
                      }}
                      label="Remove"
                    />
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </React.Fragment>
      ) : null}

      <AlertModal
        open={showLegacyApiWarningModal}
        onClose={() => setShowLegacyApiWarningModal(false)}
        trigger={null}
        title="Are you sure you want to create a legacy sixfold API key?"
        description={
          <Text>
            This API <Text weight="bold">must not be used any longer</Text> for integrations. Instead, the TP RTV API
            should be used (see{' '}
            <a href={tpRtvApiDocumentationUrl} target="_blank" rel="noopener noreferrer">
              here
            </a>
            ).
          </Text>
        }>
        <CommonUiButton kind="subtle" onClick={() => setShowLegacyApiWarningModal(false)}>
          Cancel
        </CommonUiButton>
        <CommonUiButton
          kind="primary"
          onClick={async () => {
            if (addCredentialsToUser !== undefined && user !== null) {
              await addCredentialsToUser({
                userId: user.user_id,
                type: CredentialType.API_KEY,
                value: generateRandomHexString(40),
              });
            }
            setShowLegacyApiWarningModal(false);
          }}>
          Continue
        </CommonUiButton>
      </AlertModal>
    </React.Fragment>
  );
};

function selectRoleRelevantScopes(
  value: string,
  userScopes: UserScopes,
  setUserScopes: Dispatch<SetStateAction<UserScopes>>,
) {
  const unselectAll = () => {
    setUserScopes(userScopes.map((s) => ({ ...s, isAuthorized: false })));
  };
  const selectAll = () => {
    setUserScopes(userScopes.map((s) => ({ ...s, isAuthorized: true })));
  };
  const selectAllReadonlyRoleEquivalent = () => {
    setUserScopes(
      userScopes.map((s) => ({
        ...s,
        isAuthorized: ReadOnlyRoleEquivalentScopes.includes(s.scope as BackofficeScope),
      })),
    );
  };

  unselectAll();

  if (value === 'admin') {
    selectAll();
    return;
  } else if (value === 'readonly') {
    selectAllReadonlyRoleEquivalent();
    return;
  }
}

function setPermissionsAndRole(
  level: number,
  userScopes: UserScopes,
  setUserScopes: Dispatch<SetStateAction<UserScopes>>,
  setUserRole: Dispatch<SetStateAction<string>>,
) {
  setUserRole('readonly');

  if (level === 1) {
    setUserScopes(
      userScopes.map((s) => ({
        ...s,
        isAuthorized: ReadOnlyRoleEquivalentScopes.includes(s.scope as BackofficeScope),
      })),
    );
  } else if (level === 2) {
    setUserScopes(
      userScopes.map((s) => ({
        ...s,
        isAuthorized: ![
          BackofficeScope.userReadUser,
          BackofficeScope.telematicsReadSensitiveConnectionData,
          BackofficeScope.userWriteCredentials,
        ].includes(s.scope as BackofficeScope),
      })),
    );
  } else if (level === 3) {
    setUserScopes(
      userScopes.map((s) => ({
        ...s,
        isAuthorized: true,
      })),
    );
  }
}

function convertRole(userRole: string) {
  if (userRole === '') {
    return null;
  } else if (userRole === 'admin') {
    return BackofficeRole.admin;
  } else if (userRole === 'readonly') {
    return BackofficeRole.readonly;
  }
  return null;
}
